import { ArrowLeft, ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button, SubmitButton } from "@/shared/ui/core";

export interface ProductFormActionsProps {
  submitLabel: string;
  onCancel: () => void;
  /**
   * Volver al paso anterior. Sin esto el botón no se pinta, que es lo que
   * toca en el primer paso: uno deshabilitado ocuparía sitio para no
   * hacer nada y el tabulador pasaría por él.
   */
  onBack?: () => void;
  /**
   * Avanzar. Sin esto —el último paso— no se pinta, y "Crear producto"
   * se queda como la única acción que empuja hacia adelante.
   */
  onNext?: () => void;
}

/**
 * La única fila de botones del formulario: moverse, salir y guardar.
 *
 * ── Una fila, no dos ────────────────────────────────────────────────
 * "Siguiente" vivía en una barra propia de ancho completo justo encima
 * (Anahí, 26-ago). Eran dos filas de botones para dos cosas que se leen
 * juntas, y esa barra violeta a todo lo ancho pesaba más que el propio
 * paso del formulario.
 *
 * El orden importa: "Siguiente" va PEGADO a "Crear producto" porque son
 * las dos formas de terminar —seguir o dar por bueno—, y "Cancelar"
 * queda antes, separado de la que destruye el trabajo.
 *
 * ── Por qué "Siguiente" es negro y no violeta ───────────────────────
 * En la misma fila hay dos botones llenos. Si los dos fueran violeta,
 * competirían y ninguno sería el principal. El violeta se lo queda
 * "Crear producto", que es la acción que de verdad termina el trabajo;
 * avanzar de paso es un movimiento de la interfaz.
 *
 * ── Por qué NO usa `WizardNav` ──────────────────────────────────────
 * `WizardNav` pinta su propia fila con el botón de avanzar a todo el
 * ancho que le queda, que es lo correcto cuando es la única acción de la
 * pantalla —el registro— y lo que había que deshacer aquí. Meterlo como
 * ranura dejaba "Siguiente" separado de "Crear producto" por "Cancelar".
 * Una fila la gobierna un componente.
 *
 * ── Por qué se puede guardar desde cualquier paso ───────────────────
 * Casi todo el formulario es opcional en el contrato, así que un
 * producto a medias es un producto válido. Obligar a pasar por los diez
 * pasos para poder guardar convertiría "solo quiero corregir el precio"
 * en un recorrido de diez pantallas.
 *
 * Lo que el guardado SÍ exige lo exige el contrato —un nombre y al menos
 * un precio— y si falta, el envío devuelve al paso donde se escribe, con
 * el aviso puesto. Guardar a medias es posible; guardar la nada, no.
 *
 * ── Los tipos de botón, que aquí no son un detalle ──────────────────
 * "Siguiente", "Atrás" y "Cancelar" son `type="button"` (lo es por
 * omisión en el `Button` del sistema). Dentro de un `<form>`, un botón
 * sin tipo ENVÍA: "Cancelar" acabaría guardando, y avanzar de paso
 * validaría el formulario entero sacando errores de campos que la
 * persona todavía no ha visto.
 *
 * `SubmitButton` sí es el envío de verdad, y saca su estado de carga del
 * propio formulario, así que no hay forma de dejar que se envíe dos
 * veces.
 */
export function ProductFormActions({
  submitLabel,
  onCancel,
  onBack,
  onNext,
}: ProductFormActionsProps) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-wrap items-center justify-end gap-3 border-t border-ds-border pt-3">
      <p className="mr-auto text-[12px] leading-relaxed text-ds-ink-3">
        {t("Puedes guardar sin llegar al final: lo que falte se completa después.")}
      </p>

      {onBack && (
        <Button variant="ghost" leftIcon={<ArrowLeft />} onClick={onBack}>
          {t("Atrás")}
        </Button>
      )}

      <Button variant="ghost" onClick={onCancel}>
        {t("Cancelar")}
      </Button>

      {onNext && (
        <Button variant="dark" rightIcon={<ArrowRight />} onClick={onNext}>
          {t("Siguiente")}
        </Button>
      )}

      <SubmitButton className="mt-0">{submitLabel}</SubmitButton>
    </div>
  );
}
